import { useRef, type KeyboardEvent } from "react";

/**
 * Teclado para grupos de opciones con roving tabindex (tabs, radios).
 * ←/→ (y ↑/↓ si `vertical`) mueven con ajuste circular, Inicio/Fin van a los extremos.
 * Mover la selección también mueve el foco al elemento nuevo.
 */
export function useRovingFocus<T extends string>(
  ids: ReadonlyArray<T>,
  onChange: (id: T) => void,
  { vertical = false }: { vertical?: boolean } = {},
) {
  const elements = useRef<Array<HTMLElement | null>>([]);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
    const last = ids.length - 1;
    const forward = event.key === "ArrowRight" || (vertical && event.key === "ArrowDown");
    const backward = event.key === "ArrowLeft" || (vertical && event.key === "ArrowUp");
    const target =
      forward ? (index === last ? 0 : index + 1)
        : backward ? (index === 0 ? last : index - 1)
          : event.key === "Home" ? 0
            : event.key === "End" ? last
              : -1;
    if (target < 0) return;
    const id = ids[target];
    if (id === undefined) return;
    event.preventDefault();
    onChange(id);
    elements.current[target]?.focus();
  };

  const setElement = (index: number) => (element: HTMLElement | null) => {
    elements.current[index] = element;
  };

  return { onKeyDown, setElement };
}
